// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { useSubmitting } from '@tectonics/prs/submitting';
import { Button } from '../../../commons/atoms';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import spinnerImageSrc from './spinner.svg';

export function PlaceOrderButton(): Node {
  const submitting = useSubmitting();

  return (
    <Button disabled={submitting} data-test-id="submit">
      <ButtonMessageWrapper>
        {submitting ? (
          <SpinnerWrapper>
            <Spinner src={spinnerImageSrc} alt="loading indicator" />
          </SpinnerWrapper>
        ) : (
          <ButtonMessage />
        )}
      </ButtonMessageWrapper>
    </Button>
  );
}

const SpinnerWrapper = styled.span`
  height: 1em;
  position: relative;
  :after {
    color: transparent;
    content: "Enviando...";
  }
`;

const Spinner = styled.img`
  height: 3.6em;
  left: 0;
  right: 0;
  margin: -1.2em auto;
  position: absolute;
`;

const ButtonMessageWrapper = styled.span`
  letter-spacing: 0.1em;
`;

function ButtonMessage(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return <BrazilianButtonMessage />;

    case 'mx':
      return <MexicanButtonMessage />;

    case 'co':
      return <ColombianButtonMessage />;

    case 'cl':
      return <ChileanButtonMessage />;

    case 'pe':
      return <PeruvianButtonMessage />;

    case 'ec':
      return <EcuadorianButtonMessage />;

    case 'ar':
      return <ArgentineButtonMessage />;

    case 'bo':
      return <BolivianButtonMessage />;

    case 'uy':
      return <UruguayanButtonMessage />;

    default:
      throw new Error(`PlaceOrderButton message for country: ${country} not found`);
  }
}

function BrazilianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'boleto':
      return 'Gerar boleto';

    case 'banktransfer':
    case 'pix':
      return 'Prosseguir';

    case 'vouchercard':
      return 'Confirmar';

    case '':
      return 'Selecione um método de pagamento';

    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function MexicanButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'cash':
    case 'oxxopay':
    case 'spei':
      return 'Confirmar datos';

    case '':
      return 'Elige un método de pago';

    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function ColombianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'eft':
    case 'cash':
      return 'Confirmar datos';

    case 'externalpayprovider':
      return 'Paga con PSE AVANZA';

    case '':
      return 'Elige un método de pago';

    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function ChileanButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'multicaja':
    case 'sencillito':
    case 'servipag':
    case 'webpay':
    case 'ewallet':
      return 'Confirmar datos';

    case '':
      return 'Elige un método de pago';

    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function PeruvianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'safetypay':
    case 'pagoefectivo':
      return 'Confirmar datos';

    case '':
      return 'Elige un método de pago';

    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function EcuadorianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'safetypay-cash':
    case 'safetypay-online':
      return 'Confirmar datos';

    case '':
      return 'Elige un método de pago';

    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function ArgentineButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'cash':
    case 'ewallet':
      return 'Confirmar datos';

    case '':
      return 'Elige un método de pago';

    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function BolivianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'cash':
      return 'Confirmar datos';

    case '':
      return 'Elige un método de pago';

    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function UruguayanButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'ewallet':
      return 'Confirmar datos';

    case '':
      return 'Elige un método de pago';

    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}
